export const C = { revenue: "#3B82F6", ebitda: "#14B8A6", pos: "#10B981", neg: "#F43F5E", capex: "#F97316", debt: "#8B5CF6", grey: "#64748B", opex: "#F43F5E", interest: "#A855F7" };
export const ENT_COLORS = ["#3B82F6", "#14B8A6", "#F59E0B", "#EC4899", "#22D3EE", "#A3E635", "#F97316", "#8B5CF6", "#94A3B8"];

const add = (...arrs) => arrs[0].map((_, i) => arrs.reduce((s, a) => s + (a[i] || 0), 0));
const neg = (a) => a.map((v) => -v);

// metric registry: group getter, entity getter, sections for drill-down, source reference text
export const METRICS = {
  income: { label: "Revenue", g: (g) => g.income, e: (e) => e.series.income, sections: ["income"], color: C.revenue, ref: "Σ income lines on entity sheets × NCF switch; reconciled to Fin. Sum. › Income / Revenue" },
  expenses: { label: "Operating expenses", g: (g) => neg(g.expenses), e: (e) => neg(e.series.expenses), sections: ["expenses"], color: C.opex, outflow: true, ref: "Σ expense lines (salaries, opex) on entity sheets" },
  ebitda: { label: "EBITDA", g: (g) => g.ebitda, e: (e) => e.series.ebitda, sections: ["income", "expenses"], color: C.ebitda, ref: "Operating Surplus (EBITDA) row = Total income + Total expenses" },
  capex: { label: "Total capex", g: (g) => neg(add(g.unit_capex, g.project_capex)), e: (e) => neg(add(e.series.unit_capex, e.series.project_capex)), sections: ["unit_capex", "project_capex"], color: C.capex, outflow: true, ref: "Unit capex total + Project capex total" },
  project_capex: { label: "Project capex", g: (g) => neg(g.project_capex), e: (e) => neg(e.series.project_capex), sections: ["project_capex"], color: C.capex, outflow: true, ref: "Project Capex section (net of reimbursements)" },
  interest_expense: { label: "Interest expense", g: (g) => neg(g.interest_expense), e: (e) => neg(e.series.interest_expense), sections: ["interest"], color: C.interest, outflow: true, ref: "Σ loan interest = rate × average balance (Loan Details)" },
  ncf: { label: "Net cash flow", g: (g) => g.grand_total, e: (e) => e.series.ncf, sections: [], color: C.pos, ref: "NCF sheet Grand Total (units × switch + other corporates + central funding/costs); entity view = entity sheet Net Cash Flow row" },
  closing: { label: "Closing cash", g: (g) => g.closing, e: null, sections: [], color: C.pos, balance: true, ref: "NCF › Closing Funds = Opening + Grand Total" },
  opening: { label: "Opening cash", g: (g) => g.opening, e: null, sections: [], color: C.grey, balance: true, ref: "NCF › Opening Funds (prior-year closing)" },
  debt: { label: "Outstanding debt", g: (g) => g.debt_total, e: (e) => e.series.loan_closing, sections: [], color: C.debt, balance: true, ref: "Loans sheet Grand Total / entity Loan Summary › Closing" },
  drawdown: { label: "Loan drawdown", g: (g) => g.drawdown, e: (e) => e.series.drawdown, sections: [], color: C.revenue, ref: "Loan Summary › Drawdown" },
  repayment: { label: "Loan repayment", g: (g) => neg(g.repayment), e: (e) => neg(e.series.repayment), sections: [], color: C.pos, outflow: true, ref: "Loan Summary › Repayment" },
};

export const entityGroup = (g, e) => {
  const z = g.income.map(() => 0);
  return {
    ...g, income: e.series.income, expenses: e.series.expenses, ebitda: e.series.ebitda, unit_capex: e.series.unit_capex,
    project_capex: e.series.project_capex, interest_expense: e.series.interest_expense, interest_income: e.series.interest_income,
    grand_total: e.series.ncf, drawdown: e.series.drawdown, repayment: e.series.repayment, debt_total: e.series.loan_closing,
    loan_closing: e.series.loan_closing, other_entities_ncf: z, inter_unit_funding: z, central_expense: z,
    checks: e.checks.map((c) => ({ name: `${e.short}: ${c.name}`, ok: c.ok })),
  };
};

export const metricSeries = (data, key, entityCode) => {
  const m = METRICS[key];
  if (entityCode && m.e) {
    const e = data.model.entities.find((x) => x.code === entityCode);
    if (e) return m.e(e);
  }
  return m.g(data.group);
};

export const colLetter = (i) => {
  let s = "";
  let n = i + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

// period aggregation: returns [{label, idx(first abs index), vals...}]
export const periodize = (rows, mode, balanceKeys = []) => {
  if (mode !== "3yr") return rows;
  const out = [];
  for (let i = 0; i < rows.length; i += 3) {
    const blk = rows.slice(i, i + 3);
    const r = { y: `${blk[0].y.slice(0, 2)}–${blk[blk.length - 1].y.slice(3)}`, idx: blk[0].idx };
    Object.keys(blk[0]).forEach((k) => {
      if (k === "y" || k === "idx") return;
      r[k] = balanceKeys.includes(k) ? blk[blk.length - 1][k] : blk.reduce((s, x) => s + (x[k] || 0), 0);
    });
    out.push(r);
  }
  return out;
};

export const applyUnit = (rows, unit, keys, { stacked, revenue }) => {
  if (unit === "cr") return rows;
  return rows.map((r, i) => {
    const o = { ...r };
    const tot = keys.reduce((s, k) => s + Math.abs(r[k] || 0), 0);
    keys.forEach((k) => {
      if (unit === "lakh") o[k] = (r[k] || 0) * 100;
      else o[k] = stacked ? (tot ? (r[k] / tot) * 100 : 0) : revenue?.[i] ? (r[k] / revenue[i]) * 100 : 0;
    });
    return o;
  });
};

const nf = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1, minimumFractionDigits: 1 });
export const fmtU = (v, unit) => {
  if (v == null || Number.isNaN(v)) return "–";
  if (unit === "pct") return `${v.toFixed(1)}%`;
  const s = nf.format(Math.abs(v));
  return `${v < 0 ? "−" : ""}₹${s} ${unit === "lakh" ? "L" : "Cr"}`;
};
export const unitLabel = (u) => ({ cr: "₹ crore", lakh: "₹ lakh", pct: "%" }[u]);
